import React, { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import { useCgForm } from './hooks/useCgForm';
import { useServerIssues } from './hooks/useServerIssues';
import { FormReadOnlyProvider } from './formReadOnly';

// Composable form root. Owns the react-hook-form context and the <form>
// element. Children render with <FormSection> / <FormField> / field
// components from ./fields.
//
// Either pass a `schema` (Zod) and let the form build its own methods, or
// pass an existing `methods` object from useCgForm() when the parent needs
// to drive the form imperatively (multi-step, external triggers).
const Form = forwardRef(function Form(
  {
    schema,
    defaultValues,
    methods: externalMethods,
    onSubmit,
    onInvalid,
    children,
    className = '',
    mode = 'onTouched',
    id,
    // Optional (issue, values) => fieldName override for forms whose field
    // names diverge from the backend's issue paths (phase 4 D16).
    serverFieldMap,
    // Renders every field beneath this form disabled. A field that sets its own
    // `disabled` keeps it, so this can never make a disabled field editable.
    //
    // It is a PRESENTATION guard, not an authorization one: form state is still
    // reachable through `methods`, so a host that must not write must also not
    // call the thing that writes. See EdgeEditDrawer, which switches its
    // autosave off rather than relying on the fields being quiet.
    readOnly = false,
  },
  ref,
) {
  // Built-in server-issue seam (phase 4): a failed submit's `issues[]` can be
  // handed back via ref.setServerIssues(err) and renders as per-field errors.
  // Only wired for internally-built methods — a caller passing its own
  // `methods` composes useServerIssues into its own resolver instead.
  const serverIssues = useServerIssues({ schema, map: serverFieldMap });
  const internalMethods = useCgForm({
    schema,
    defaultValues,
    mode,
    resolver: serverIssues.resolver,
    // Skip the internal hook entirely when the caller already has methods.
    // useForm is happy to be created and unused; the cost is negligible and
    // it keeps the hook order stable.
  });
  const methods = externalMethods || internalMethods;

  useImperativeHandle(
    ref,
    () => ({
      submit: () => methods.handleSubmit(onSubmit, onInvalid)(),
      getValues: methods.getValues,
      setValue: methods.setValue,
      reset: methods.reset,
      methods,
      // Returns the UNMATCHED issues (D17f) — surface those via banner/toast.
      setServerIssues: externalMethods ? null : (err) => serverIssues.setServerIssues(err, methods),
      clearServerIssues: externalMethods ? null : serverIssues.clearServerIssues,
    }),
    [methods, onSubmit, onInvalid, externalMethods, serverIssues],
  );

  return (
    <FormProvider {...methods}>
      <FormReadOnlyProvider value={readOnly}>
        <form
          id={id}
          className={className}
          onSubmit={methods.handleSubmit(onSubmit, onInvalid)}
          noValidate
        >
          {children}
        </form>
      </FormReadOnlyProvider>
    </FormProvider>
  );
});

export default Form;
